import React, { FC, useEffect, useState } from 'react';
import { DialogProps } from "@mui/material";
import CheckmateDialogBase from "../CheckmateDialogBase/CheckmateDialogBase";
import CheckmateButton from "../FormControls/Button/Button";
import { useDispatch, useSelector } from 'react-redux';
import { setToastMessage } from '../../../main/slices/utilSlice';
import './SupportModal.scss';
import CheckmateTextInput from '../FormControls/CheckmateTextInput/CheckmateTextInput';
import CheckmateSelect from '../FormControls/CheckmateSelect/CheckmateSelect';
import styles from '../../../assets/styles/variables.module.scss';
import { callCreateSupportTicket, callGetUserTeamDetails, selectUserDetails } from '../../../main/slices/userSlice';
import { callGetUserDetails } from '../../../main/slices/userSlice';
import { callGetLoggedInUser, selectLoggedInUser } from '../../../main/slices/authSlice';

interface SupportIssue {
    label: string;
    category: 'userSupport' | 'technicalSupport' | 'licensingBilling';
    value: string; 
}

const SUPPORT_ISSUES: SupportIssue[] = [
    // User Support Issues
    { label: 'Forgot Password', category: 'userSupport', value: 'FORGOT_PASSWORD' },
    { label: 'Account Locked', category: 'userSupport', value: 'ACCOUNT_LOCKED' },
    { label: 'Login Issues', category: 'userSupport', value: 'LOGIN_ISSUES' },

    // Technical Support Issues
    { label: 'Documents Not Uploading', category: 'technicalSupport', value: 'DOCUMENTS_ARENT_UPLOADING' },
    { label: 'Report Generation Issue', category: 'technicalSupport', value: 'REPORT_DOES_NOT_GENERATE' },
    { label: 'System Down', category: 'technicalSupport', value: 'ATAI_SEEMS_TO_BE_DOWN' },
    { label: 'Batch Query Error', category: 'technicalSupport', value: 'BATCH_QUERY_ERROR' },
    { label: 'Page Not Loading', category: 'technicalSupport', value: 'PAGE_NOT_LOADING' },
    { label: 'Query Not Running', category: 'technicalSupport', value: 'QUERY_ISNT_RUNNING' },

    // Licensing and Billing Issues
    { label: "Didn't Receive Invoice", category: 'licensingBilling', value: 'DIDNT_RECEIVE_INVOICE' },
    { label: 'Setup Auto-Pay', category: 'licensingBilling', value: 'WANTS_TO_SETUP_AUTO_PAY' },
    { label: 'Billing Questions', category: 'licensingBilling', value: 'BILLING_QUESTIONS' },
    { label: 'Change Product Level', category: 'licensingBilling', value: 'PRODUCT_LEVEL_CHANGE_REQUEST' },
    { label: 'Renewal Request', category: 'licensingBilling', value: 'RENEWAL_REQUEST' },
    { label: 'Billing Cycle Issue', category: 'licensingBilling', value: 'BILLING_CYCLE_ISSUE' },
    { label: 'Refund Request', category: 'licensingBilling', value: 'REFUND_REQUEST' },
    { label: 'Change Payment Method', category: 'licensingBilling', value: 'PAYMENT_METHOD_CHANGE' },
    { label: 'Other - Describe Issue Below', category: 'technicalSupport', value: 'OTHER_ISSUE' }
];

const CategoryLabels = [
    { label: 'User Support', value: 'userSupport' },
    { label: 'Technical Support', value: 'technicalSupport' },
    { label: 'Licensing and Billing', value: 'licensingBilling' }
];

interface SupportModalProps extends DialogProps {
    onClose: (event: any, reason?: string) => void;
}

const SupportModal: FC<SupportModalProps> = ({ open, onClose, ...dialogProps }) => {
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        issueType: '',
        browserType: '',
        product: ''
    });

    const userDetailsRedux = useSelector(selectLoggedInUser);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            name: userDetailsRedux?.displayName || '',
            email: userDetailsRedux?.email || ''
        }));
        
    }, [userDetailsRedux]);

    const userDetails = userDetailsRedux;

    
    const teamIds = userDetails?.teams?.map((team: any) => team.id).join(',');

    useEffect(() => {
        dispatch(callGetUserTeamDetails(teamIds || '') as any).then((response: any) => {
            const filteredTeams = response.payload.data.filter((team: any) => teamIds?.includes(team.id));
            const tierType = filteredTeams.map((team: any) => team.tierType);
            if (tierType.includes('Premium')) {
                setFormData(prev => ({
                    ...prev,
                    product: 'atai premium'
                }));
            } else if (tierType.includes('Advanced')) {
                setFormData(prev => ({
                    ...prev,
                    product: 'atai advanced'
                }));
            } else{
                setFormData(prev => ({
                    ...prev,
                    product: 'atai essential'
                }));
            }
        });
    }, [userDetails]);

    const handleSubmit = async () => {
        const selectedIssue = SUPPORT_ISSUES.find(issue => issue.label === formData.issueType);
        
        if (!selectedIssue) return;
        const requestData = {
            userFullName: formData.name,
            // customer: formData.company,
            userEmail: userDetails?.email || '',
            phoneNumber: formData.phone || '',
            summary: `${CategoryLabels.find(category => category.value === selectedIssue.category)?.label} - ${selectedIssue.label}`,
            description: formData.message,
            browser: formData.browserType.toLowerCase(),
            [selectedIssue.category]: selectedIssue.value,
            product: formData.product
        };
        dispatch(callCreateSupportTicket(requestData) as any).then((response: any) => {
        if (response.meta.requestStatus === 'fulfilled') {
            dispatch(setToastMessage({
                message: 'Support request submitted successfully',
                type: 'success'
            }));
            handleClose();
        } else {    
            dispatch(setToastMessage({
                message: 'Error submitting support request',
                type: 'error'
            }));
        }
        });
    };

    const handleInputChange = (fieldName: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
        
        if (fieldName === 'company' || fieldName === 'phone') {
            const newLength = value.length;
            
            if (newLength === 255) {
                dispatch(setToastMessage({
                    message: `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} field is limited to 255 characters.`,
                    type: 'warning'
                }));
            }
        } else if (fieldName === 'message') {
            const newLength = value.length;
            if (newLength === 32767) {
                dispatch(setToastMessage({
                    message: 'Description field is limited to 32,767 characters.',
                    type: 'warning'
                }));
            }
        }
    };

    const handleClose = () => {
        setFormData(prev => ({
            ...prev,
            // company: '',
            phone: '',
            message: '',
            issueType: '',
            browserType: ''
        }));
        onClose(false, 'submitClick');
    };

    const isFormValid = () => {
        return formData.name && 
               formData.email &&
               formData.phone &&
            //    formData.company &&
               formData.message && 
               formData.issueType &&
               formData.browserType;
    };

    return (
        userDetails && <CheckmateDialogBase
            {...dialogProps}
            open={open}
            title="Atai Technical Support"
            maxWidth={'xl'}
            fullWidth={true}
            onClose={handleClose}
            centerTitle={true}
            modalContent={
                <div className="support-form-wrapper">
                    <p className="support-description" style={{textAlign: 'center'}}>
                        Welcome to Atai Support. Our world-class team is dedicated to resolving your issue. For swift, expert support, submit your issue via the form. You're backed by our innovation team that sets the standard.
                    </p>
                    <div className="form-grid">
                        <div className="form-field">
                            <CheckmateTextInput
                                value={formData.name}
                                label={'Name'}
                                fieldName={'name'}
                                onChange={value => handleInputChange('name', value)}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                readOnly={true}
                            />
                        </div>
                        {/* <div className="form-field">
                            <CheckmateTextInput
                                value={formData.company}
                                label={'Company'}
                                fieldName={'company'}
                                onChange={value => handleInputChange('company', value)}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                maxLength={255}
                            />
                        </div> */}
                        <div className="form-field">
                            <CheckmateTextInput
                                value={formData.email}
                                label={'Email'}
                                fieldName={'email'}
                                onChange={value => handleInputChange('email', value)}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                readOnly={true}
                            />
                        </div>
                        <div className="form-field">
                            <CheckmateTextInput
                                value={formData.phone}
                                label={'Phone'}
                                fieldName={'phone'}
                                onChange={value => handleInputChange('phone', value)}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                maxLength={255}
                            />
                        </div>
                        <div className="form-field">
                            <CheckmateSelect
                                label="Issue Type"
                                fieldName="issueType"
                                value={formData.issueType}
                                listItems={SUPPORT_ISSUES.map(issue => issue.label)}
                                onChange={(value, fieldName) => {
                                    handleInputChange(fieldName, value as string);
                                }}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                focusBorderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                            />
                        </div>
                        <div className="form-field">
                            <CheckmateSelect
                                label="Browser Type"
                                fieldName="browserType"
                                value={formData.browserType}
                                listItems={['Chrome', 'Firefox', 'Safari', 'Edge', 'Internet Explorer', 'Opera', 'Other']}
                                onChange={(value, fieldName) => {
                                    handleInputChange(fieldName, value as string);
                                }}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                focusBorderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                            />
                        </div>

                        <div className="form-field full-width">
                            <CheckmateTextInput
                                value={formData.message}
                                label={'Detailed description of issue'}
                                fieldName={'message'}
                                multiline={true}
                                growWithContent={true}
                                onChange={value => handleInputChange('message', value)}
                                color={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_on_background : 'default'}
                                focusColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_focus_border : 'default'}
                                borderColor={process.env.REACT_APP_SKIN_NAME === 'atai' ? styles.color_tertiary : 'default'}
                                maxLength={32767}
                            />
                        </div>
                    </div>
                </div>
            }
            actionButtons={
                <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                    <CheckmateButton
                        variant="contained"
                        color="primary"
                        label="Send"
                        onClick={handleSubmit}
                        disabled={!isFormValid()}
                    />
                </div>
            }
        />
    );
};

export default SupportModal; 